import type { SessionsListResult } from "../types.ts";
import { parseAgentSessionKey } from "../../../../src/routing/session-key.js";
import { normalizeChatOpenTabs } from "./chat-tabs.ts";

/** Four colon-derived segments of a session key (agent → agentId → kind → id). */
export type SessionKeyPath = [string, string, string, string];

const PATH_DEPTH = 4 as const;

export function parseSessionKeyPath(sessionKey: string): SessionKeyPath {
  const trimmed = sessionKey.trim();
  if (!trimmed) {
    return ["", "", "", ""];
  }
  const parsed = parseAgentSessionKey(trimmed);
  if (parsed) {
    const colon = parsed.rest.indexOf(":");
    if (colon < 0) {
      return ["agent", parsed.agentId, parsed.rest, ""];
    }
    return ["agent", parsed.agentId, parsed.rest.slice(0, colon), parsed.rest.slice(colon + 1)];
  }
  const parts = trimmed.split(":").filter((p) => p.length > 0);
  return [parts[0] ?? "", parts[1] ?? "", parts[2] ?? "", parts.slice(3).join(":") || ""];
}

export function sessionKeyFromPath(path: SessionKeyPath): string | null {
  const [p0, p1, p2, p3] = path.map((s) => s.trim());
  if (!p0 || !p1 || !p2) {
    return null;
  }
  if (p0 === "agent") {
    return p3 ? `agent:${p1}:${p2}:${p3}` : `agent:${p1}:${p2}`;
  }
  if (!p3) {
    return p2 ? `${p0}:${p1}:${p2}` : p1 ? `${p0}:${p1}` : p0;
  }
  return `${p0}:${p1}:${p2}:${p3}`;
}

export function collectSessionKeysForPicker(params: {
  activeSessionKey: string;
  openTabs: string[];
  sessions: SessionsListResult | null;
}): string[] {
  const seen = new Set<string>();
  const keys: string[] = [];
  const push = (raw: string) => {
    const key = raw.trim();
    if (!key || seen.has(key)) {
      return;
    }
    seen.add(key);
    keys.push(key);
  };
  for (const key of normalizeChatOpenTabs(params.openTabs, params.activeSessionKey)) {
    push(key);
  }
  push(params.activeSessionKey);
  for (const row of params.sessions?.sessions ?? []) {
    if (row.key) {
      push(row.key);
    }
  }
  return keys;
}

function pathMatchesPrefix(
  path: SessionKeyPath,
  prefix: SessionKeyPath,
  throughLevel: number,
): boolean {
  for (let i = 0; i <= throughLevel; i++) {
    const want = prefix[i]?.trim();
    if (!want) {
      continue;
    }
    if ((path[i] ?? "").trim() !== want) {
      return false;
    }
  }
  return true;
}

export function uniquePathSegmentAtLevel(
  keys: string[],
  level: number,
  prefix: SessionKeyPath,
): string[] {
  const values = new Set<string>();
  for (const key of keys) {
    const path = parseSessionKeyPath(key);
    if (!pathMatchesPrefix(path, prefix, level - 1)) {
      continue;
    }
    const segment = (path[level] ?? "").trim();
    if (segment) {
      values.add(segment);
    }
  }
  return [...values].sort((a, b) => a.localeCompare(b));
}

export function matchingSessionKeys(
  keys: string[],
  prefix: SessionKeyPath,
  throughLevel: number,
): string[] {
  return keys.filter((key) => pathMatchesPrefix(parseSessionKeyPath(key), prefix, throughLevel));
}

export function pickSessionKeyForPathClick(params: {
  keys: string[];
  activeSessionKey: string;
  prefix: SessionKeyPath;
  level: number;
  value: string;
  sessions: SessionsListResult | null;
}): string | null {
  const next: SessionKeyPath = [...params.prefix];
  next[params.level] = params.value.trim();
  for (let i = params.level + 1; i < PATH_DEPTH; i++) {
    next[i] = "";
  }

  const updatedAt = (key: string) =>
    params.sessions?.sessions?.find((s) => s.key === key)?.updatedAt ?? 0;

  if (params.level === 3) {
    const exact = sessionKeyFromPath(next);
    if (exact && params.keys.includes(exact)) {
      return exact;
    }
    const candidates = matchingSessionKeys(params.keys, next, 3);
    return (
      candidates.find((k) => parseSessionKeyPath(k)[3] === next[3]) ??
      [...candidates].sort((a, b) => updatedAt(b) - updatedAt(a))[0] ??
      null
    );
  }

  const candidates = matchingSessionKeys(params.keys, next, params.level);
  if (candidates.length === 0) {
    return null;
  }

  const exactFromPath = sessionKeyFromPath(next);
  if (exactFromPath && candidates.includes(exactFromPath)) {
    return exactFromPath;
  }

  if (candidates.includes(params.activeSessionKey)) {
    const activePath = parseSessionKeyPath(params.activeSessionKey);
    if (pathMatchesPrefix(activePath, next, params.level)) {
      return params.activeSessionKey;
    }
  }

  const leafCandidates = candidates.filter((key) => {
    const path = parseSessionKeyPath(key);
    if (params.level >= 2 && !next[3]) {
      return !path[3];
    }
    return true;
  });
  const pool = leafCandidates.length > 0 ? leafCandidates : candidates;
  return [...pool].sort((a, b) => updatedAt(b) - updatedAt(a))[0] ?? null;
}

export function formatPathSegmentLabel(segment: string, level: number): string {
  const trimmed = segment.trim();
  if (!trimmed) {
    return "—";
  }
  if (level === 3 && trimmed.length > 12) {
    return `${trimmed.slice(0, 8)}…`;
  }
  return trimmed;
}

export const SESSION_PATH_LEVEL_LABELS = ["1 · scope", "2 · agent", "3 · kind", "4 · id"] as const;
